import { useCallback, useEffect, useMemo, useState } from 'react';

import { useLocation, useNavigate, useParams } from 'react-router-dom';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import LogoutIcon from '@mui/icons-material/Logout';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import ListItemIcon from '@mui/material/ListItemIcon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { useLogoutMutation } from '@/api/auth/authApi';
import type { VerifyOtpResponse } from '@/api/auth/types';
import { useGetFfneByIdQuery } from '@/api/ffne/ffneApi';
import { config } from '@/config/env';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { clearUser } from '@/store/slices/authSlice';
import { getInitialsFromName } from '@/utils/getUserInitials';
import {
  getStoredHeaderProjectName,
  getStoredHeaderProposalName,
  persistHeaderProjectName,
  persistHeaderProposalName,
  type HeaderNavigationState,
} from '@/utils/headerContext';

import { getErrorMessage, useToast } from '@/components/atoms/Toast';

function displayName(user: VerifyOtpResponse): string {
  const n = user.first_name?.trim();
  if (n) return n;
  return user.emailId?.split('@')[0] ?? 'User';
}

export function AppHeader() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const dispatch = useAppDispatch();
  const toast = useToast();
  const user = useAppSelector(s => s.auth.user);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [logoFailed, setLogoFailed] = useState(false);
  const open = Boolean(anchorEl);

  const [logout, { isLoading }] = useLogoutMutation();
  const projectId = params.id ?? '';
  const ffneId = params.ffneId ?? '';
  const routeHeaderState = (location.state as HeaderNavigationState | null) ?? null;
  const { data: ffneDetails } = useGetFfneByIdQuery(ffneId, { skip: !ffneId });

  const handleCloseMenu = useCallback(() => setAnchorEl(null), []);

  const handleLogout = useCallback(async () => {
    handleCloseMenu();
    await logout()
      .unwrap()
      .catch(err => toast.error(getErrorMessage(err)))
      .finally(() => {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('user');
        localStorage.removeItem('userDetails');
        dispatch(clearUser());
        navigate('/login', { replace: true });
      });
  }, [dispatch, handleCloseMenu, logout, navigate, toast]);
  const storedProjectName = useMemo(() => getStoredHeaderProjectName(projectId), [projectId]);
  const storedProposalName = useMemo(
    () => getStoredHeaderProposalName(projectId, ffneId),
    [ffneId, projectId],
  );
  const headerTitle = useMemo(() => {
    const routeProjectName = routeHeaderState?.headerProjectName?.trim() ?? '';
    const routeProposalName = routeHeaderState?.headerProposalName?.trim() ?? '';
    const fetchedProposalName = ffneDetails?.proposalName?.trim() ?? '';
    const resolvedProjectName = routeProjectName || storedProjectName;

    if (ffneId) {
      return routeProposalName || fetchedProposalName || storedProposalName || resolvedProjectName;
    }

    if (projectId) {
      return resolvedProjectName ? `${resolvedProjectName}  (Versioning)` : '';
    }

    return '';
  }, [
    ffneDetails?.proposalName,
    ffneId,
    projectId,
    routeHeaderState,
    storedProjectName,
    storedProposalName,
  ]);

  useEffect(() => {
    if (projectId && routeHeaderState?.headerProjectName) {
      persistHeaderProjectName(projectId, routeHeaderState.headerProjectName);
    }
  }, [projectId, routeHeaderState?.headerProjectName]);

  useEffect(() => {
    if (projectId && ffneId && routeHeaderState?.headerProposalName) {
      persistHeaderProposalName(projectId, ffneId, routeHeaderState.headerProposalName);
    }
  }, [ffneId, projectId, routeHeaderState?.headerProposalName]);

  useEffect(() => {
    if (projectId && ffneId && ffneDetails?.proposalName) {
      persistHeaderProposalName(projectId, ffneId, ffneDetails.proposalName);
    }
  }, [ffneDetails?.proposalName, ffneId, projectId]);

  if (!user) {
    return null;
  }

  const name = displayName(user);
  const initials = getInitialsFromName(name);

  return (
    <Stack
      component="header"
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 'appBar',
        bgcolor: '#fff',
        borderBottom: '1px solid #ececec',
        px: { xs: 2, sm: 3, md: '52px' },
        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.15), 0 1px 2px -1px rgba(0, 0, 0, 0.10)',
        height: 52,
      }}
    >
      <Stack
        direction="row"
        height="100%"
        alignItems="center"
        justifyContent="space-between"
        spacing={2}
      >
        {headerTitle ? (
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 700,
              color: '#1e2a39',
              lineHeight: 1.2,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: { xs: '45vw', md: '52vw' },
            }}
          >
            {headerTitle}
          </Typography>
        ) : user.accountLogo && !logoFailed ? (
          <Box
            component="img"
            src={user.accountLogo}
            alt=""
            sx={{ height: 23, width: 110, objectFit: 'contain', objectPosition: 'left' }}
            onError={() => setLogoFailed(true)}
          />
        ) : (
          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1e2a39' }}>
            {config.appTitle}
          </Typography>
        )}

        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ ml: 'auto' }}>
          {/* <IconButton size="small" sx={{ color: '#1e2a39' }} aria-label="Notifications">
            <Badge color="error" variant="dot" overlap="circular">
              <NotificationsNoneOutlinedIcon sx={{ fontSize: 22 }} />
            </Badge>
          </IconButton> */}

          <Divider orientation="vertical" flexItem sx={{ borderColor: '#ececec', my: 0.5 }} />

          <Stack
            direction="row"
            alignItems="center"
            spacing={1.25}
            onClick={e => setAnchorEl(e.currentTarget)}
            sx={{
              cursor: 'pointer',
              py: 0.5,
              pr: 0.5,
              borderRadius: 1,
              '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' },
            }}
            role="button"
            tabIndex={0}
            aria-haspopup="true"
            aria-expanded={open ? 'true' : undefined}
          >
            {user.img_url ? (
              <Avatar
                src={user.img_url}
                alt=""
                sx={{ width: 28, height: 28, bgcolor: '#2d3748' }}
              />
            ) : (
              <Avatar
                sx={{ width: 28, height: 28, bgcolor: '#2d3748', fontSize: 12, fontWeight: 600 }}
              >
                {initials}
              </Avatar>
            )}
            <Stack spacing={0} sx={{ display: { xs: 'none', sm: 'flex' }, minWidth: 0 }}>
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, color: '#1e2a39', lineHeight: 1.2, whiteSpace: 'nowrap' }}
              >
                {name}
              </Typography>
              <Typography variant="caption" sx={{ color: '#718096', lineHeight: 1.2 }} noWrap>
                {user.role}
              </Typography>
            </Stack>
            <KeyboardArrowDownIcon sx={{ fontSize: 20, color: '#718096' }} />
          </Stack>
        </Stack>
      </Stack>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: { mt: 1, minWidth: 160, borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' },
          },
        }}
      >
        <MenuItem onClick={handleLogout} disabled={isLoading} sx={{ py: 1.25 }}>
          <ListItemIcon>
            {isLoading ? <CircularProgress size={18} /> : <LogoutIcon fontSize="small" />}
          </ListItemIcon>
          Logout
        </MenuItem>
      </Menu>
    </Stack>
  );
}
